import { HttpResponse } from '@angular/common/http';
import { Component, DOCUMENT, HostListener, inject, OnInit } from '@angular/core';
import { RegulatoryDocumentConsentControllerService, RegulatoryDocumentMetaDto } from '@core/api';
import { DownloadService } from '@core/service/download.service';
import { SnackBarService } from '@core/service/snack-bar.service';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { UnsubscribeOnDestroyAdapter } from '@shared/unsubscribe-on-destroy-adapter';
import { NgcCookieConsentConfig, NgcCookieConsentModule, NgcCookieConsentService, NgcInitializationErrorEvent, NgcInitializingEvent, NgcNoCookieLawEvent, NgcStatusChangeEvent, WindowService } from 'ngx-cookieconsent';
import { map } from 'rxjs';

@Component({
    selector: 'app-cookie-consent',
    template: '',
    imports: [
        NgcCookieConsentModule,
    ],
    providers:[
        // provideNgcCookieConsent(cookieConfig), // with fix config
        NgcCookieConsentService,
        WindowService,
        NgcCookieConsentConfig,
    ]
})
export class CookieConsentComponent extends UnsubscribeOnDestroyAdapter implements OnInit {
    @HostListener('document:click', ['$event']) onDocumentClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (target && target.id === 'cookie-policy-link') {
            event.preventDefault(); // Stop the '#' jump
            this.downloadLatest();
        }
    }    
    
    private readonly _ccService = inject(NgcCookieConsentService);
    private readonly _translate = inject(TranslateService);
    private readonly _regulatoryDocumentConsentControllerService = inject(RegulatoryDocumentConsentControllerService);
    private readonly _downloadService = inject(DownloadService);
    private readonly _snackBarService = inject(SnackBarService);
    private readonly _document = inject(DOCUMENT);

    private _cookieConfig: NgcCookieConsentConfig = {
        cookie: {
            domain: this._document.location.hostname, // it is recommended to set your domain, for cookies to work properly
        },
        palette: {
            popup: {
                background: 'inherit',
                text: '#000000',
                link: 'defaultColor',
            },
            button: {
                background: 'defaultColor',
                text: '#ffffff',
                border: 'transparent',
            }
        },
        theme: 'classic',
        type: 'info',
        layout: 'my-custom-layout',
        layouts: { // custom layout
            "my-custom-layout": '{{messagelink}}{{compliance}}'
        },
        elements:{
            messagelink: `
                <span id="cookieconsent:desc" class="cc-message">{{message}} 
                    <a aria-label="{{link}}" tabindex="1" class="cc-link" id="cookie-policy-link" href="{{href}}" target="_blank" rel="noopener">{{link}}</a> 
                </span>
            `,
        },
        content:{
            message: this._translate.instant('cookie.message'),
            link: this._translate.instant('cookie.link'),
            href: '#',
            dismiss: this._translate.instant('cookie.dismiss'),
        }
    };

    constructor() {
        super();
        this.initCookieConsent(this._cookieConfig);
    }

    ngOnInit() {
        // this.setCookieConsentLanguage(); // use this if no custom layout is used
        this.createSubscriptions();
    }

    private createSubscriptions(){
        this.subs.add(
            this._translate.onLangChange.subscribe((event: LangChangeEvent) => {
                this.setCookieConsentCustomLayoutLanguage(); // use this if the custom layout is used
            })
        );
        this.setCookieConsentSubscriptions();
    }

    // // use this if no custom layout is used
    // private setCookieConsentLanguage(){
    //     // Support for translated cookies messages
    //     this._translate.addLangs(['hu', 'en']);
    //     this._translate.setFallbackLang('hu');

    //     const browserLang = this._translate.getBrowserLang();
    //     if(browserLang){
    //         this._translate.use(browserLang.match(/hu|en/) ? browserLang : 'hu');
    //     }

    //     this._translate.get(['cookie.header', 'cookie.message', 'cookie.dismiss', 'cookie.allow', 'cookie.deny', 'cookie.link', 'cookie.policy']).subscribe({
    //         next: (data) => {
    //             this._ccService.getConfig().content = this._ccService.getConfig().content || {} ;
    //             const content = this._ccService.getConfig().content;
    //             if(content){
    //                 console.dir('override');
    //                 // Override default messages with the translated ones
    //                 content.header = data['cookie.header'];
    //                 content.dismiss = data['cookie.dismiss'];
    //                 content.allow = data['cookie.allow'];
    //                 content.deny = data['cookie.deny'];
    //                 content.link = data['cookie.link'];
    //                 content.policy = data['cookie.policy'];
    //                 content.message = data['cookie.message'];
    //             }
    //             this.initCookieConsent(this._ccService.getConfig());
    //         }
    //     });
    // }

    // use this if a custom layout is used
    private setCookieConsentCustomLayoutLanguage(){
        if(this._cookieConfig.content){
            this._cookieConfig.content.message = this._translate.instant('cookie.message');
            this._cookieConfig.content.link = this._translate.instant('cookie.link');
            this._cookieConfig.content.dismiss = this._translate.instant('cookie.dismiss');
        }
        this.initCookieConsent(this._cookieConfig);
    }

    private initCookieConsent(cookieConfig: NgcCookieConsentConfig){
        this._ccService.destroy(); // remove previous cookie bar (with default messages)
        this._ccService.init(cookieConfig); // update config with translated messages
    }

    private downloadLatest(){
        this.subs.add(
            this._regulatoryDocumentConsentControllerService.downloadLatest(RegulatoryDocumentMetaDto.DocumentTypeEnum.Gdpr, 'response').pipe(
                map((res: HttpResponse<string>) => {
                    const blobParts = Array.isArray(res.body) ? res.body : [res.body || ''];
                    const blob = new Blob(blobParts, { type: 'application/octet-stream' });
                    return res.clone({ body: blob });
                }),
            )
            .subscribe({
                next: (response: HttpResponse<Blob>) => {
                    this._downloadService.download(response, 'GDPR.pdf');
                },
                error: (err) => {
                    this._snackBarService.errorMessage('shared.adddocumentdownloadtolink.error', this.selectDocumentName(RegulatoryDocumentMetaDto.DocumentTypeEnum.Gdpr));
                }
            })
        );
    }

    private selectDocumentName(documentType: RegulatoryDocumentMetaDto.DocumentTypeEnum): Record<string, string>{
        const param: Record<string, string> = {
            documentType: this._translate.instant(`enum.documentType.${documentType}`),
        }
        return param;
    }

    private setCookieConsentSubscriptions(){
        this.subs.add(
            // subscribe to cookieconsent observables to react to main events
                this._ccService.popupOpen$.subscribe(
                () => {
                    // you can use this._ccService.getConfig() to do stuff...
                }
            ),

            this._ccService.popupClose$.subscribe(
                () => {
                    // you can use this._ccService.getConfig() to do stuff...
                }
            ),

            this._ccService.initializing$.subscribe(
                (event: NgcInitializingEvent) => {
                    // the cookieconsent is initilializing... Not yet safe to call methods like `NgcCookieConsentService.hasAnswered()`
                    // console.log(`initializing: ${JSON.stringify(event)}`);
                }
            ),
            
            this._ccService.initialized$.subscribe(
                () => {
                    // the cookieconsent has been successfully initialized.
                    // It's now safe to use methods on NgcCookieConsentService that require it, like `hasAnswered()` for eg...
                    // console.log(`initialized: ${JSON.stringify(event)}`);
                }
            ),

            this._ccService.initializationError$.subscribe(
                (event: NgcInitializationErrorEvent) => {
                    // the cookieconsent has failed to initialize... 
                    // console.log(`initializationError: ${JSON.stringify(event.error?.message)}`);
                }
            ),

            this._ccService.statusChange$.subscribe(
                (event: NgcStatusChangeEvent) => {
                    // you can use this._ccService.getConfig() to do stuff...
                }
            ),

            this._ccService.revokeChoice$.subscribe(
                () => {
                    // you can use this._ccService.getConfig() to do stuff...
                }
            ),

            this._ccService.noCookieLaw$.subscribe(
                (event: NgcNoCookieLawEvent) => {
                    // you can use this._ccService.getConfig() to do stuff...
                }
            )
        );
    }
}
